import axios from 'axios';

// iter70 — Hardened API base resolution.
//
// Two production misconfigurations seen so far:
//   1. `REACT_APP_BACKEND_URL` unset → axios falls back to a relative
//      baseURL `/api` → every call is made relative to the current SPA
//      route (e.g. `/register/api/auth/me`).
//   2. `REACT_APP_BACKEND_URL=https://myleaflegacy.com/register`
//      (operator copied the current URL by mistake) → baseURL becomes
//      `https://myleaflegacy.com/register/api` — same broken result.
//
// To make these structurally impossible, we:
//   a. Treat the env var as a hint, not a contract.
//   b. Always strip any path component (`new URL(envBase).origin`).
//   c. Fall back to `window.location.origin` when env is empty,
//      malformed, or fails to parse.
// The frontend + backend always share the same host on Emergent
// deployments, so `window.location.origin` is the correct base in
// every realistic scenario.
function _resolveApiBase() {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (raw) {
    try {
      // `new URL` validates AND extracts origin only — strips path,
      // search, hash. This is the single point that defends against
      // `.../register` style misconfiguration.
      const u = new URL(raw);
      if (u.origin && u.origin !== 'null') return u.origin;
    } catch {
      /* malformed env var — fall through to runtime origin */
    }
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }
  return '';
}

const BACKEND_URL = _resolveApiBase();
export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// iter65 — Bearer-token fallback.
// Browsers (notably Safari with ITP, and some custom-domain proxy setups)
// silently drop the httponly auth cookie between requests. When that
// happens we still hold a valid JWT in localStorage from the OAuth or
// password-login exchange — attach it as `Authorization: Bearer …` so
// the next call to /api/auth/me succeeds and the user doesn't get
// bounced back to /login mid-flow.
api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('token');
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    /* storage disabled — fall back to cookie-only */
  }
  return config;
});

// When the server confirms our token is stale/invalid, clear localStorage
// so we don't keep re-sending a dead Bearer header forever. The page
// itself decides whether to redirect (ProtectedRoute already does).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const url = err.config?.url || '';
      // Only clear on /auth/me — every other 401 (e.g. permission denied
      // on a non-auth endpoint) leaves the session intact.
      if (url.includes('/auth/me')) {
        try { localStorage.removeItem('token'); } catch { /* noop */ }
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiError(e, fallback = 'Something went wrong. Please try again.') {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((x) => (x && typeof x.msg === 'string' ? x.msg : JSON.stringify(x))).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
